import { useRef, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { UFS, type EmailPrefs } from '@pmis/shared';
import { api, errorMessage } from '../lib/api';
import { entraAvailable, saveEntraPhoto, useAuth } from '../lib/auth';
import { squarePhotoBase64 } from '../lib/files';
import { Alert, Button, Dialog, Field, Input, Select, Spinner } from '../components/ui';
import { Avatar, useToast } from '../components/pmis';

interface MailData { prefs: EmailPrefs; notices: readonly { key: keyof EmailPrefs; label: string; description: string }[] }

/**
 * Perfil: nome de exibição, cargo, celular, estado onde trabalha (feriados estaduais), foto (enviada ou trazida da conta
 * Microsoft) e os avisos por e-mail que a pessoa quer receber.
 */
export function ProfileDialog({ onClose }: { onClose: () => void }) {
  const { me, reloadMe } = useAuth();
  const toast = useToast();
  const qc = useQueryClient();
  const [name, setName] = useState(me?.displayName ?? '');
  const [title, setTitle] = useState(me?.jobTitle ?? '');
  const [phone, setPhone] = useState(me?.phone ?? '');
  const [uf, setUf] = useState<string>(me?.homeUf ?? '');
  const mail = useQuery({ queryKey: ['email-prefs'], queryFn: () => api<MailData>('/api/v1/me/email-prefs') });
  const [prefs, setPrefs] = useState<EmailPrefs | null>(null);
  const curPrefs = prefs ?? mail.data?.prefs ?? null;
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const file = useRef<HTMLInputElement>(null);
  if (!me) return null;
  const run = async (fn: () => Promise<unknown>, ok: string, close = false) => {
    setBusy(true); setError(null);
    try { await fn(); await reloadMe(); toast(ok); if (close) onClose(); } catch (e) { setError(errorMessage(e)); } finally { setBusy(false); }
  };
  const save = () => run(async () => {
    await api('/api/v1/me/profile', { method: 'PUT', body: { displayName: name, jobTitle: title || null, phone: phone || null, homeUf: uf || null } });
    if (prefs) { await api('/api/v1/me/email-prefs', { method: 'PUT', body: prefs }); await qc.invalidateQueries({ queryKey: ['email-prefs'] }); }
  }, 'Perfil salvo.', true);
  return (
    <Dialog open onClose={onClose} title="Meu perfil" wide footer={<>
      <Button onClick={onClose}>Fechar</Button>
      <Button variant="primary" disabled={busy} onClick={save}>Salvar</Button>
    </>}>
      <div className="space-y-4">
        {error ? <Alert tone="err">{error}</Alert> : null}
        <div className="flex items-center gap-4">
          <Avatar userId={me.id} name={me.displayName} version={me.photoVersion} size={72} />
          <div className="flex flex-wrap gap-2">
            <input ref={file} type="file" accept="image/*" hidden onChange={(e) => {
              const f = e.target.files?.[0];
              if (f) void run(async () => api('/api/v1/me/photo', { method: 'PUT', body: { dataBase64: await squarePhotoBase64(f), source: 'upload' } }), 'Foto atualizada.');
              e.target.value = '';
            }} />
            <Button size="sm" onClick={() => file.current?.click()} disabled={busy}>Enviar foto</Button>
            {entraAvailable() ? <Button size="sm" disabled={busy} onClick={() => run(async () => { if (!(await saveEntraPhoto(true))) throw new Error('A sua conta Microsoft não tem foto.'); }, 'Foto trazida da conta Microsoft.')}>Usar a foto da Microsoft</Button> : null}
            {me.photoVersion ? <Button size="sm" variant="ghost" disabled={busy} onClick={() => run(() => api('/api/v1/me/photo', { method: 'DELETE' }), 'Foto removida.')}>Remover</Button> : null}
          </div>
        </div>
        <Field label="Nome de exibição"><Input value={name} onChange={(e) => setName(e.target.value)} /></Field>
        <div className="grid gap-3 sm:grid-cols-3">
          <Field label="Cargo"><Input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Ex.: Gerente de projetos" /></Field>
          <Field label="Celular"><Input value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="(11) 9…" /></Field>
          <Field label="Estado onde trabalho (UF)" hint="Os feriados do seu estado tiram a sua capacidade e entram nos projetos remotos que você instala.">
            <Select value={uf} onChange={(e) => setUf(e.target.value)}><option value="">—</option>{UFS.map(([c, n]) => <option key={c} value={c}>{c} · {n}</option>)}</Select>
          </Field>
        </div>
        <dl className="grid grid-cols-[120px_1fr] gap-y-1 rounded-lg bg-panel-2 p-3 text-sm">
          <dt className="text-muted">E-mail</dt><dd>{me.email}</dd>
          <dt className="text-muted">Perfis</dt><dd>{me.roleNames.join(', ')}</dd>
          <dt className="text-muted">Entrada</dt><dd>{me.authMethod === 'entra' ? 'Conta Microsoft' : me.authMethod === 'password' ? 'E-mail e senha' : 'Desenvolvimento'}</dd>
          <dt className="text-muted">Capacidade</dt><dd>{me.capacityHoursDay} h por dia</dd>
        </dl>
        <fieldset className="rounded-xl border border-line p-4">
          <legend className="px-1.5 text-sm font-semibold">Avisos por e-mail</legend>
          {!curPrefs || !mail.data ? <Spinner /> : (
            <div className="space-y-2 text-sm">
              <p className="text-xs text-muted">Escolha o que chega no seu e-mail. Os avisos no sistema (o sininho, no alto) continuam do mesmo jeito.</p>
              {mail.data.notices.map((n) => (
                <label key={n.key} className="flex cursor-pointer items-start gap-3 rounded-lg border border-line p-3 hover:bg-panel-2">
                  <input type="checkbox" className="mt-1" checked={curPrefs[n.key]} onChange={(e) => setPrefs({ ...curPrefs, [n.key]: e.target.checked })} />
                  <span><span className="block font-medium">{n.label}</span><span className="block text-xs text-muted">{n.description}</span></span>
                </label>
              ))}
              <p className="text-xs text-muted">O e-mail sai pela caixa do Microsoft 365 da empresa. Se o Administrador desligar um aviso para todos, ele não chega mesmo que esteja marcado aqui.</p>
            </div>
          )}
        </fieldset>
      </div>
    </Dialog>
  );
}
